import React, {useState, useEffect} from 'react';
import {View, ViewStyle, StyleSheet, Image} from 'react-native';
//styling related and ui-kits
import colors from 'theme/Colors';
import {spacing} from 'theme/Units';
import {Text} from 'ui-kits/Text';
import {Icon} from 'ui-kits/Icon';
import {ToggleButton} from 'ui-kits/Button/ToggleButton';
//navigation
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
//selector and slices relatd imports
import {
  selectedEmployeeData,
  employeeFailure,
  employeeSuccess,
} from 'redux/selectors/employeeSelector';
import {getOrgID} from 'redux/selectors/authSelector';
import {EmployeeForm} from 'components';
import {defaultUserProfile} from 'constants/DefaultValues';
//api related
import {useDispatch, useSelector} from 'react-redux';
import {useToast} from 'hooks';
import {SUCCESS_KEY, ERROR_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {
  updateEmployeeDetailsRequest,
  resetMessage,
} from 'redux/slices/employeeSlice';
import {getFormattedDate} from 'utils/getFormattedDate';

type initialValuesType = {
  id: number | null;
  emp_id: string;
  first_name: string;
  last_name: string;
  profile: string;
  date_of_joining: Date | null;
  dob: Date | null;
  last_day_of_work: Date | null;
  is_active: boolean;
  project: any[];
};

const initialValues: initialValuesType = {
  id: null,
  emp_id: '',
  first_name: '',
  last_name: '',
  date_of_joining: null,
  dob: null,
  last_day_of_work: null,
  profile: '',
  is_active: false,
};

export function UpdateEmployeeDetails({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const org_id = useSelector(getOrgID);
  const {showToasts} = useToast();
  const dispatch = useDispatch();
  const selectedEmployee = useSelector(selectedEmployeeData);
  const updateEmployeeSuccess = useSelector(employeeSuccess);
  const updateEmployeeError = useSelector(employeeFailure);
  const [isEmployeeActive, setIsEmployeeActive] = useState<boolean>(
    selectedEmployee?.is_active || false,
  );

  const [formValues, setFormValues] =
    useState<initialValuesType>(initialValues);
  // error message toast
  useEffect(() => {
    if (updateEmployeeError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(updateEmployeeError as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, updateEmployeeError]);

  // success message toast
  useEffect(() => {
    if (updateEmployeeSuccess) {
      if (updateEmployeeSuccess === 'S-10019') {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(updateEmployeeSuccess as SuccessCodes),
          'success',
        );
        navigation.navigate(Routes.VIEW_EMPLOYEE_DETAILS, {
          id: selectedEmployee?.id.toString()!,
        });
      }
      dispatch(resetMessage());
    }
  }, [
    dispatch,
    showToasts,
    updateEmployeeSuccess,
    navigation,
    selectedEmployee,
  ]);

  useEffect(() => {
    if (selectedEmployee) {
      setFormValues({
        ...selectedEmployee,
        emp_id: selectedEmployee.emp_id!,
        dob: new Date(selectedEmployee.dob as any),
        date_of_joining: new Date(selectedEmployee.date_of_joining as any),
        last_day_of_work: selectedEmployee.last_day_of_work
          ? new Date(selectedEmployee.last_day_of_work as any)
          : null,
        is_active: selectedEmployee.is_active,
        profile: selectedEmployee.profile_photo_url,
        last_name: selectedEmployee.last_name ? selectedEmployee.last_name : '',
      });
      setIsEmployeeActive(selectedEmployee?.is_active as any);
    }
  }, [selectedEmployee]);

  const handleCancel = () => {
    goBack();
  };

  const handleSubmit = ({id, project, ...values}: initialValuesType) => {
    const payload = {
      ...values,
      dob: getFormattedDate(values.dob, 'yyyy-mm-dd', '-'),
      date_of_joining: getFormattedDate(values.date_of_joining, 'yyyy-mm-dd', '-'),
      last_day_of_work: getFormattedDate(values.last_day_of_work, 'yyyy-mm-dd', '-'),
      org_id,
      id: id,
      is_active: isEmployeeActive,
    };
    dispatch(updateEmployeeDetailsRequest(payload as any));
  };
  function handleToggle() {
    setIsEmployeeActive(p => !p);
  }
  function goBack() {
    navigation.navigate(Routes.VIEW_EMPLOYEE_DETAILS, {
      id: selectedEmployee?.id.toString()!,
    });
  }
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text variant="header" style={{color: colors.palette.brandBlue}}>
          Update Employee Details
        </Text>
        <Icon type={'back_arrow'} onPress={goBack} />
      </View>
      <View style={styles.main}>
        {/* profile details */}
        <View style={styles.innerContainer}>
          <View style={styles.profileSection}>
            <Image
              resizeMode="cover"
              source={{
                uri: selectedEmployee?.profile_photo_url! ?? defaultUserProfile,
              }}
              style={styles.profileImage}
            />
            <View style={styles.basicDetails}>
              <Text variant="sourcesans16">{selectedEmployee?.emp_id!}</Text>
              <Text variant="inter24">
                {selectedEmployee?.first_name!}{' '}
                {selectedEmployee?.last_name ? selectedEmployee?.last_name : ''}
              </Text>
              <View style={styles.toggle}>
                <ToggleButton
                  value={isEmployeeActive}
                  onToggle={handleToggle}
                />
                <Text variant="sourcesans16">
                  {selectedEmployee?.is_active! ? 'Active' : 'Inactive'}
                </Text>
              </View>
            </View>
          </View>

          {/* update form */}
          <EmployeeForm
            initialValues={formValues}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEditing={true}
          />
        </View>
      </View>
    </View>
  );
}
type styleTypes = {
  container: ViewStyle;
  header: ViewStyle;
  basicDetails: ViewStyle;
  profileSection: ViewStyle;
  profileImage: ViewStyle;
  main: ViewStyle;
  toggle: ViewStyle;
};
const styles: styleTypes = StyleSheet.create({
  container: {
    flex: 1,
    paddingVertical: spacing['6'],
    paddingHorizontal: spacing['8'],
    gap: spacing['6'],
  },
  header: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  main: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  innerContainer: {
    width: '60%',
  },
  basicDetails: {
    gap: spacing['1'],
  },
  profileSection: {
    flexDirection: 'row',
    gap: spacing['8'],
    alignItems: 'center',
  },
  profileImage: {
    width: 140,
    height: 140,
    borderRadius: 999,
  },
  toggle: {
    flexDirection: 'row',
    gap: spacing['3'],
    alignContent: 'center',
  },
});
